import React, { useEffect, useState } from 'react';
import { styles } from './RecentlyViewed.css';
import { ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { useInterval } from '@jbhi-fi/utils/src/useInterval/useInterval';
import { AlgoliaProductHit } from '../../../../utils/types';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { SmartModuleSettings } from '../../../../../global';
import { useAlternativesContext } from '@jbhi-fi/components/src/ProductAlternatives/AlternativesContextProvider';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { ProductSuggestionsContainer } from '@jbhi-fi/components/src/ProductSuggestionsContainer';

const recentlyViewedProducts = JSON.parse(localStorage.getItem('recentlyViewed') as string);
const sortByMostRecent = (a: AlgoliaProductHit, b: AlgoliaProductHit) => {
  const skuA = a.sku || a.skus?.[0];
  const skuB = b.sku || b.skus?.[0];
  return recentlyViewedProducts.indexOf(Number(skuA)) - recentlyViewedProducts.indexOf(Number(skuB));
};

type RecentlyViewedProps = {
  data?: Array<AlgoliaProductHit>;
  settings?: SmartModuleSettings;
  location?: string;
  page: string;
  isEnhancedGoogleRecommendationsEnabled?: boolean;
  variant?: 'carousel' | 'list';
};

const RecentlyViewed = ({
  data,
  settings,
  location,
  page,
  isEnhancedGoogleRecommendationsEnabled,
  variant = 'carousel',
}: RecentlyViewedProps): JSX.Element => {
  const filteredAlgoliaData = data?.sort(sortByMostRecent).slice(0, settings?.productCount) ?? [];
  const [shouldShowRecentlyViewed, setShouldShowRecentlyViewed] = useState(settings?.isEnabled ?? false);
  const hasLoaded = data?.length;
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const { handleViewAlternatives } = useAlternativesContext();
  const isAlternativesEnabled = settings?.isAlternativesEnabled && isEnhancedGoogleRecommendationsEnabled;

  const ecommerceExtraProps = {
    list: 'Recently Viewed',
    itemListSlot: String(location),
    itemListServiceConfig: null,
  };
  const trackingDescriptor = {
    page,
    group: 'recentlyViewed',
    ecommerceExtraProps: ecommerceExtraProps,
  };
  const handleLoadingTimeout = () => {
    if (!hasLoaded) {
      setShouldShowRecentlyViewed(false);
    }
  };

  const wishlistProps = !isEnhancedGoogleRecommendationsEnabled
    ? {
        isLoggedIn,
        onUpdateWishlist: handleUpdateWishlist,
        getIsInWishlist,
      }
    : {};

  useInterval(handleLoadingTimeout, window.smartModuleSettings?.loadingTimeout || 5000, true);

  useEffect(() => {
    const productImpressions = filteredAlgoliaData?.map((hit, i) => ({
      ...hit.google_tag_manager,
      list: 'dynamic-recentlyViewed',
      position: i,
    }));

    if (productImpressions?.length) {
      window.gtm.track('productImpression', {
        products: productImpressions,
      });
    }
  }, [data]);

  const handleAddToCart = async (e: React.SyntheticEvent, hit: AlgoliaProductHit) => {
    if (!onAddToCart) return false;
    const isSuccess = await onAddToCart(e, hit, hit.variant_id, trackingDescriptor, 'alternatives');
    return isSuccess;
  };

  return (
    <>
      {shouldShowRecentlyViewed && filteredAlgoliaData.length ? (
        <div className={styles.recentlyViewedContainer} data-testid={`${page}-recently-viewed-products-container`}>
          {location === 'cart' ? (
            <h3 className={styles.cartHeading}>{settings?.title}</h3>
          ) : variant === 'list' ? (
            <CalloutFont as="h3" testId={`${page}-recently-viewed-products-title`}>
              {settings?.title}
            </CalloutFont>
          ) : (
            <CalloutFont className={styles.heading({ variant })} testId={`${page}-recently-viewed-products-title`}>
              {settings?.title}
            </CalloutFont>
          )}
          {variant === 'list' ? (
            <ProductSuggestionsContainer
              tileVariant="minified"
              suggestions={filteredAlgoliaData}
              onAddToCart={handleAddToCart}
              type="recentlyViewed"
              trackingProps={ecommerceExtraProps}
            />
          ) : (
            <ProductCarousel
              fullBleed={false}
              trackingDescriptor={trackingDescriptor}
              className={styles.carousel}
              fullHeightItems
              products={filteredAlgoliaData}
              renderProduct={({ hit, trackingDescriptor }) => (
                <ProductCard
                  {...AlgoliaHitTransformer.toProductCardProps(hit, { trackingDescriptor })}
                  {...productCartPropsOverrides}
                  variant={isEnhancedGoogleRecommendationsEnabled ? 'minified' : 'tablet'}
                  getIsCompared={isAddedToCompare}
                  onCompare={compareHandleClick}
                  getShouldShowCompare={shouldShowCompare}
                  onViewAlternatives={
                    isAlternativesEnabled
                      ? () =>
                          handleViewAlternatives(
                            hit,
                            settings.alternativesCount ?? 4,
                            'alternatives',
                            ecommerceExtraProps
                          )
                      : undefined
                  }
                  onAddToCart={
                    onAddToCart
                      ? (e, _sku, variantId, eventData) => onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
                      : undefined
                  }
                  {...wishlistProps}
                />
              )}
            />
          )}
        </div>
      ) : null}
    </>
  );
};

export default RecentlyViewed;
